/**
 * LoggerContext - React context + hook for web logging
 *
 * Usage:
 *   <LoggerProvider appSource="web-student" apiBaseUrl="https://...">
 *     <App />
 *   </LoggerProvider>
 *
 *   const logger = useLogger(WebComponentCode.WEB_API, 'fetchQuestions');
 *   logger.info('Loaded questions');
 */

import { createContext, useContext, useEffect, useRef, useMemo, type ReactNode } from 'react';
import { WebComponentCode, type WebAppSource } from './types';
import { WebLogger } from './WebLogger';
import { getWebTracingManager } from './WebTracingManager';
import { WebConsoleProvider } from './providers/WebConsoleProvider';
import { WebServerProvider } from './providers/WebServerProvider';
import { WebPopupProvider } from './providers/WebPopupProvider';
import { loadWebTracingConfig } from './configLoader';

interface LoggerContextValue {
  getLogger: (component: WebComponentCode, operation?: string) => WebLogger;
}

const LoggerContext = createContext<LoggerContextValue | null>(null);

interface LoggerProviderProps {
  appSource: WebAppSource;
  apiBaseUrl: string;
  getAuthToken?: () => string | null;
  children: ReactNode;
}

export function LoggerProvider({ appSource, apiBaseUrl, getAuthToken, children }: LoggerProviderProps) {
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const manager = getWebTracingManager();
    manager.setSource(appSource);

    // Register providers
    manager.registerProvider(new WebConsoleProvider());
    manager.registerProvider(new WebServerProvider(apiBaseUrl, getAuthToken));
    manager.registerProvider(new WebPopupProvider());

    // Load remote config (async, non-blocking)
    loadWebTracingConfig(apiBaseUrl).then((config) => {
      if (config) manager.updateConfig(config);
    });

    // Global error handlers
    const handleError = (event: ErrorEvent) => {
      const logger = new WebLogger(WebComponentCode.WEB_UI, 'global');
      logger.error(event.message || 'Unhandled error', event.error, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const logger = new WebLogger(WebComponentCode.WEB_UI, 'global');
      const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      logger.error('Unhandled promise rejection', reason);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
      manager.flush();
    };
  }, [appSource, apiBaseUrl, getAuthToken]);

  const value = useMemo<LoggerContextValue>(
    () => ({
      getLogger: (component: WebComponentCode, operation?: string) => new WebLogger(component, operation),
    }),
    []
  );

  return <LoggerContext.Provider value={value}>{children}</LoggerContext.Provider>;
}

/**
 * Hook to get a WebLogger instance for a specific component/operation.
 * The logger is memoized per component+operation pair.
 */
export function useLogger(component: WebComponentCode, operation?: string): WebLogger {
  const context = useContext(LoggerContext);

  return useMemo(() => {
    if (context) {
      return context.getLogger(component, operation);
    }
    // Fallback: return a logger that works without context (for use in non-React code)
    return new WebLogger(component, operation);
  }, [context, component, operation]);
}
